import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import { NavLink, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  Brain,
  Layers,
  ClipboardCheck,
  Network,
  GitBranch,
  Archive,
  ShieldCheck,
  Settings,
  ChevronRight,
  Search,
  Circle,
  Activity,
  Cpu,
  BookOpen,
  Stethoscope,
  HeartPulse,
  Wallet,
  Sparkles,
  Lock,
  ArrowUpRight,
  AlertTriangle,
  FlaskConical,
} from "lucide-react";

/* ================================================================
   NAV — no "Home". Users enter Executive Intelligence directly.
   Pluggable: append to NAV + register a <Route/>.
   ================================================================ */
const NAV = [
  { key: "eic",      path: "/app",                        label: "Executive Intelligence", Icon: Brain },
  { key: "divs",     path: "/app/divisions",              label: "Intelligence Divisions", Icon: Layers },
  { key: "review",   path: "/app/workspace",              label: "Founder Workspace",      Icon: ClipboardCheck },
  { key: "topology", path: "/app/neural-topology",        label: "Neural Topology",        Icon: Network },
  { key: "graph",    path: "/app/knowledge-graph",        label: "Knowledge Graph",        Icon: GitBranch },
  { key: "memory",   path: "/app/institutional-memory",   label: "Institutional Memory",   Icon: Archive },
  { key: "gov",      path: "/app/governance",             label: "Governance",             Icon: ShieldCheck },
  { key: "settings", path: "/app/settings",               label: "Settings",               Icon: Settings },
];

/* ============================================================
   Reusable primitives
   ============================================================ */
const PageTitle = ({ eyebrow, title, description, right }) => (
  <header className="page-title-block">
    <div>
      {eyebrow && <div className="eyebrow-mono">{eyebrow}</div>}
      <h1 className="eio-title">{title}</h1>
      {description && <p className="eio-desc">{description}</p>}
    </div>
    {right}
  </header>
);

const Panel = ({ children, className = "", ...rest }) => (
  <section className={`eio-panel ${className}`} {...rest}>{children}</section>
);

const StatusDot = ({ tone = "ok" }) => <span className={`dot dot-${tone}`} />;

const Divider = () => <div className="eio-divider" />;

const KMono = ({ children }) => <span className="k-mono">{children}</span>;

const EmptyState = ({ Icon = Sparkles, title, hint }) => (
  <div className="eio-empty">
    <div className="eio-empty-icon"><Icon size={20} /></div>
    <div className="eio-empty-title">{title}</div>
    {hint && <div className="eio-empty-hint">{hint}</div>}
  </div>
);

/* ============================================================
   Living neural topology — canvas, continuously evolving
   ============================================================ */
const LivingTopology = ({ intensity = "normal" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext("2d");
    let raf;
    let nodes = [];
    const setup = () => {
      const r = c.getBoundingClientRect();
      c.width = r.width; c.height = r.height;
      const count = intensity === "dense" ? 60 : 34;
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * c.width,
        y: Math.random() * c.height,
        vx: (Math.random() - 0.5) * 0.28,
        vy: (Math.random() - 0.5) * 0.28,
        r: 1.4 + Math.random() * 1.6,
        pulse: Math.random() * Math.PI * 2,
        weight: Math.random(),
      }));
    };
    setup();
    window.addEventListener("resize", setup);

    const draw = (t) => {
      ctx.clearRect(0, 0, c.width, c.height);
      // edges
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i], b = nodes[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < 140) {
            const alpha = 0.14 * (1 - d / 140);
            ctx.strokeStyle = `rgba(0,229,204,${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }
      // nodes
      nodes.forEach((n) => {
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > c.width) n.vx *= -1;
        if (n.y < 0 || n.y > c.height) n.vy *= -1;
        const pulse = (Math.sin(t / 700 + n.pulse) + 1) / 2;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r + pulse * 1.2, 0, Math.PI * 2);
        ctx.fillStyle = n.weight > 0.85
          ? `rgba(201,168,76,${0.35 + pulse * 0.5})`
          : `rgba(0,229,204,${0.28 + pulse * 0.45})`;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", setup); };
  }, [intensity]);
  return <canvas ref={ref} className="topology-canvas" aria-hidden="true" />;
};

/* ================================================================
   EXECUTIVE INTELLIGENCE CENTER
   ================================================================ */
const OPERATIONAL_WIDGETS = [
  { key: "system-health",     label: "System Health",         value: "Nominal",   tone: "ok",   sub: "0 alerts · 42ms p95" },
  { key: "exec-activity",     label: "Executive Activity",    value: "6 / 6",     tone: "ok",   sub: "All officers reporting" },
  { key: "knowledge-growth",  label: "Knowledge Growth",      value: "+2.4%",     tone: "ok",   sub: "12,481 nodes · 24h" },
  { key: "institutional-learning", label: "Institutional Learning", value: "12 lessons", tone: "info", sub: "This quarter" },
  { key: "platform-status",   label: "Platform Status",       value: "3 online",  tone: "ok",   sub: "ProsthoAI · Reimburse · Zida" },
  { key: "clinical-intel",    label: "Clinical Intelligence", value: "94%",       tone: "ok",   sub: "Reasoning accuracy · 7d" },
  { key: "revenue-intel",     label: "Revenue Intelligence",  value: "91%",       tone: "ok",   sub: "First-pass claim rate · 7d" },
  { key: "security-status",   label: "Security Status",       value: "Guarded",   tone: "watch",sub: "HIPAA architecture in progress" },
  { key: "pending-reviews",   label: "Pending Founder Reviews", value: "4",       tone: "watch",sub: "1 high-risk awaiting synthesis" },
  { key: "strategic-alerts",  label: "Strategic Alerts",      value: "2",         tone: "info", sub: "Both non-urgent · this week" },
];

const EXECUTIVES = [
  {
    role: "Chief Intelligence Officer", short: "CIO", Icon: Brain, tone: "teal",
    objective: "Synthesise founder review DEC-2041 across all officers",
    confidence: 88,
    workload: 3,
    recentRec: "Consolidate British-English narrative into ZidaMD default (approved)",
    status: "In session",
    upcoming: "Executive Decision Report · DEC-2041",
  },
  {
    role: "Chief Medical Officer", short: "CMO", Icon: HeartPulse, tone: "teal",
    objective: "Review physician-facing risk calculators for ICD-10 2026",
    confidence: 92,
    workload: 2,
    recentRec: "Approve ICD-10 2026 refresh cycle in ZidaMD",
    status: "Standby",
    upcoming: "Q3 clinical evidence sweep",
  },
  {
    role: "Chief Dental Officer", short: "CDO", Icon: Stethoscope, tone: "teal",
    objective: "Codify prosthodontic decision rules for D2740 documentation",
    confidence: 90,
    workload: 4,
    recentRec: "Require periapical evidence on all D2740 auto-drafts",
    status: "In session",
    upcoming: "Full-mouth planning schema v2",
  },
  {
    role: "Chief Technology Officer", short: "CTO", Icon: Cpu, tone: "gold",
    objective: "Prepare Bridge service architecture for review",
    confidence: 86,
    workload: 5,
    recentRec: "Adopt SHA-256 patient bridge hash for de-identified beta",
    status: "In session",
    upcoming: "Ratify Bridge v0.1 architecture spec",
  },
  {
    role: "Chief Security & Compliance Officer", short: "CSCO", Icon: Lock, tone: "gold",
    objective: "Confirm HIPAA readiness gates before non-synthetic data",
    confidence: 74,
    workload: 6,
    recentRec: "Hold non-synthetic patient IDs until BAA is executed",
    status: "Flagged",
    upcoming: "MongoDB Atlas BAA follow-up",
  },
  {
    role: "Chief Business Officer", short: "CBO", Icon: Wallet, tone: "gold",
    objective: "Evaluate revenue impact of denial-prevention feedback loop",
    confidence: 89,
    workload: 2,
    recentRec: "Projected +4.2 day payment-cycle improvement · Aetna PPO",
    status: "Standby",
    upcoming: "Q3 practice-health benchmark",
  },
];

const ExecIntelligencePage = () => (
  <div>
    <PageTitle
      eyebrow="Executive Intelligence"
      title="Command Center"
      description="Operational intelligence across every CamelionAI division and executive officer."
      right={
        <div className="header-status">
          <StatusDot tone="ok" /> <KMono>OPERATIONAL</KMono>
          <span className="header-time" data-testid="header-time">
            {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase()}
          </span>
        </div>
      }
    />

    <div className="widget-grid" data-testid="operational-widgets">
      {OPERATIONAL_WIDGETS.map((w) => (
        <Panel key={w.key} className={`widget widget-${w.tone}`} data-testid={`widget-${w.key}`}>
          <div className="widget-label">{w.label}</div>
          <div className="widget-value">{w.value}</div>
          <div className="widget-sub"><StatusDot tone={w.tone} /> {w.sub}</div>
        </Panel>
      ))}
    </div>

    <div className="section-heading">
      <div>
        <div className="eyebrow-mono">Executive Board</div>
        <h2 className="eio-h2">Six officers · One institution</h2>
      </div>
      <span className="section-hint">Cards update as officers advance their objectives.</span>
    </div>

    <div className="board-grid">
      {EXECUTIVES.map((e) => (
        <Panel key={e.short} className={`board-card board-${e.tone}`} data-testid={`board-${e.short.toLowerCase()}`}>
          <div className="board-head">
            <div className="board-avatar">
              <e.Icon size={18} />
            </div>
            <div className="board-role-wrap">
              <div className="board-role">{e.role}</div>
              <div className="board-short">{e.short}</div>
            </div>
            <div className={`board-status board-status-${e.status.toLowerCase().replace(/\s/g,"-")}`}>
              <StatusDot tone={e.status === "Flagged" ? "watch" : e.status === "In session" ? "ok" : "info"} />
              {e.status}
            </div>
          </div>
          <div className="board-body">
            <div className="board-row">
              <div className="board-row-label">Current objective</div>
              <div className="board-row-val">{e.objective}</div>
            </div>
            <div className="board-metrics">
              <div className="metric">
                <div className="metric-label">Confidence</div>
                <div className="metric-value">{e.confidence}<span className="metric-suffix">/100</span></div>
                <div className="metric-bar"><div className="metric-fill" style={{ width:`${e.confidence}%` }} /></div>
              </div>
              <div className="metric">
                <div className="metric-label">Review workload</div>
                <div className="metric-value">{e.workload}</div>
                <div className="metric-sub">active items</div>
              </div>
            </div>
            <div className="board-row board-row-quote">
              <div className="board-row-label">Recent recommendation</div>
              <div className="board-row-val board-quote">{e.recentRec}</div>
            </div>
            <div className="board-row">
              <div className="board-row-label">Upcoming</div>
              <div className="board-row-val board-upcoming">{e.upcoming}</div>
            </div>
          </div>
        </Panel>
      ))}
    </div>
  </div>
);

/* ================================================================
   INTELLIGENCE DIVISIONS
   ================================================================ */
const DIVISIONS = [
  {
    key: "clinical",
    name: "Clinical Intelligence Division",
    surface: "ProsthoAI",
    Icon: Stethoscope,
    href: "https://prosthoai.com",
    contributes: "Case reasoning · treatment rationales · outcome patterns",
    receives:    "Denial signals · documentation quality · payer rules",
    tone: "prostho",
  },
  {
    key: "revenue",
    name: "Revenue Intelligence Division",
    surface: "ReimbursePro",
    Icon: Wallet,
    href: "https://reimburse.pro",
    contributes: "Denial patterns · payer behaviour · reimbursement forecasts",
    receives:    "Clinical evidence · documentation completeness · prior-auth rules",
    tone: "reimburse",
  },
  {
    key: "physician",
    name: "Physician Intelligence Division",
    surface: "ZidaMD",
    Icon: HeartPulse,
    href: "https://zidamd.com",
    contributes: "Clinical reasoning traces · differential quality · ICD-10 fluency",
    receives:    "Cross-specialty knowledge · outcome patterns · evidence updates",
    tone: "zida",
  },
  {
    key: "future",
    name: "Future Divisions",
    surface: "Reserved",
    Icon: Sparkles,
    href: null,
    contributes: "—",
    receives:    "—",
    tone: "muted",
  },
];

const DivisionsPage = () => (
  <div>
    <PageTitle
      eyebrow="Intelligence Divisions"
      title="Organisational intelligence"
      description="Each division contributes intelligence to CamelionAI — and receives intelligence from it."
    />
    <div className="div-grid">
      {DIVISIONS.map((d) => (
        <Panel key={d.key} className={`div-card div-${d.tone}`} data-testid={`div-${d.key}`}>
          <div className="div-head">
            <div className="div-icon"><d.Icon size={18} /></div>
            <div>
              <div className="div-name">{d.name}</div>
              <div className="div-surface">{d.surface}</div>
            </div>
            {d.href && (
              <a className="div-open" href={d.href} target="_blank" rel="noopener noreferrer">
                Open <ArrowUpRight size={12} />
              </a>
            )}
          </div>
          <Divider />
          <div className="div-io">
            <div>
              <div className="div-io-label">Contributes</div>
              <div className="div-io-body">{d.contributes}</div>
            </div>
            <div>
              <div className="div-io-label">Receives</div>
              <div className="div-io-body">{d.receives}</div>
            </div>
          </div>
        </Panel>
      ))}
    </div>
  </div>
);

/* ================================================================
   FOUNDER WORKSPACE — decision workspace (lazy-loaded)
   ================================================================ */
const FounderWorkspace = lazy(() => import("./workspace/FounderWorkspace"));
const DeliberationView = lazy(() => import("./workspace/DeliberationView"));

const WorkspaceRoute = () => (
  <Suspense fallback={<div className="nt-loading">Opening Decision Workspace…</div>}>
    <FounderWorkspace />
  </Suspense>
);

const DeliberationRoute = () => (
  <Suspense fallback={<div className="nt-loading">Convening the Executive Board…</div>}>
    <DeliberationView />
  </Suspense>
);

/* ================================================================
   NEURAL TOPOLOGY — lazy-loaded 3D scene (three.js)
   ================================================================ */
const NeuralTopology3D = lazy(() => import("./NeuralTopology3D"));

const TopologyRoute = () => (
  <Suspense fallback={<div className="nt-loading">Loading Neural Topology Engine…</div>}>
    <NeuralTopology3D />
  </Suspense>
);

/* ================================================================
   NEURAL TOPOLOGY — placeholder in-shell view (kept for reference)
const TopologyPage = () => (
  <div>
    <PageTitle
      eyebrow="Neural Topology"
      title="Living reasoning fabric"
      description="How intelligence flows across knowledge, evidence, clinical reasoning, revenue intelligence, security, research, learning, and recommendations."
      right={
        <div className="header-status">
          <StatusDot tone="ok" /> <KMono>LIVE</KMono>
        </div>
      }
    />
    <Panel className="topology-panel">
      <LivingTopology intensity="dense" />
      <div className="topology-legend">
        {[
          { c: "#00E5CC", l: "Knowledge · Evidence" },
          { c: "#00E5CC", l: "Clinical reasoning" },
          { c: "#00E5CC", l: "Revenue intelligence" },
          { c: "#C9A84C", l: "Security · Governance" },
          { c: "#C9A84C", l: "Institutional memory" },
        ].map((x) => (
          <span key={x.l} className="legend"><span className="legend-dot" style={{ background: x.c, boxShadow: `0 0 6px ${x.c}` }} /> {x.l}</span>
        ))}
      </div>
    </Panel>
    <p className="page-footnote">The topology evolves naturally as officers reason, submit reviews, and reach decisions. Reasoning engine attaches in a future release.</p>
  </div>
);

/* ================================================================
   KNOWLEDGE GRAPH — relationships between entities
   ================================================================ */
const KG_ENTITY_TYPES = [
  { k: "Procedure",  count: 412,   Icon: ClipboardCheck },
  { k: "Diagnosis",  count: 288,   Icon: Activity },
  { k: "Payer",      count: 34,    Icon: Wallet },
  { k: "Evidence",   count: 1_204, Icon: BookOpen },
  { k: "Decision",   count: 41,    Icon: Brain },
  { k: "Pattern",    count: 63,    Icon: Sparkles },
];

const KG_RELATIONSHIPS = [
  { l: "Procedure",  op: "requires",           r: "Evidence",  strength: 0.92 },
  { l: "Procedure",  op: "may trigger",        r: "Prior Auth",strength: 0.68 },
  { l: "Denial",     op: "informs",            r: "Pattern",   strength: 0.88 },
  { l: "Pattern",    op: "surfaces as",        r: "Alert",     strength: 0.81 },
  { l: "Decision",   op: "governed by",       r: "Policy",    strength: 0.95 },
];

const GraphPage = () => (
  <div>
    <PageTitle
      eyebrow="Knowledge Graph"
      title="Relationships"
      description="Structured relationships between clinical, revenue, and organisational entities. The topology governs how intelligence flows across these relationships."
      right={
        <div className="table-search">
          <Search size={13} />
          <input placeholder="Search entities…" disabled />
        </div>
      }
    />

    <div className="kg-grid">
      <Panel className="kg-entities">
        <div className="kg-block-head">
          <div className="eyebrow-mono">Entities</div>
          <KMono>{KG_ENTITY_TYPES.reduce((s, x) => s + x.count, 0).toLocaleString()} total</KMono>
        </div>
        <div className="kg-entity-list">
          {KG_ENTITY_TYPES.map((e) => (
            <div key={e.k} className="kg-entity">
              <span className="kg-entity-icon"><e.Icon size={14} /></span>
              <span className="kg-entity-name">{e.k}</span>
              <span className="kg-entity-count">{e.count.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </Panel>

      <Panel className="kg-rels">
        <div className="kg-block-head">
          <div className="eyebrow-mono">Relationships</div>
          <KMono>schema · v1</KMono>
        </div>
        <div className="kg-rel-list">
          {KG_RELATIONSHIPS.map((r, i) => (
            <div key={i} className="kg-rel">
              <span className="kg-rel-l">{r.l}</span>
              <span className="kg-rel-op">— {r.op} →</span>
              <span className="kg-rel-r">{r.r}</span>
              <span className="kg-rel-strength">
                <span className="strength-bar"><span style={{ width: `${r.strength * 100}%` }} /></span>
                {Math.round(r.strength * 100)}
              </span>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  </div>
);

/* ================================================================
   INSTITUTIONAL MEMORY — knowledge promotion workflow (lazy-loaded)
   ================================================================ */
const InstitutionalMemory = lazy(() => import("./memory/InstitutionalMemory"));

const MemoryRoute = () => (
  <Suspense fallback={<div className="nt-loading">Opening Institutional Memory…</div>}>
    <InstitutionalMemory />
  </Suspense>
);

/* ================================================================
   GOVERNANCE
   ================================================================ */
const GOV_SECTIONS = [
  { k: "policies",    title: "Executive policies",       hint: "Decision authority · escalation ladders." },
  { k: "ai",          title: "AI governance",            hint: "Model registry · reasoning traces · human approval." },
  { k: "security",    title: "Security policies",        hint: "Access · encryption · vulnerability response." },
  { k: "hipaa",       title: "HIPAA readiness",          hint: "Architecture posture · BAA status · gates." },
  { k: "audit",       title: "Audit logs",               hint: "Immutable · 7-year retention." },
  { k: "versions",    title: "Version history",          hint: "Every policy and decision, immutably versioned." },
  { k: "adr",         title: "Architectural Decision Records", hint: "ADR-0001 through ADR-0008." },
  { k: "approvals",   title: "Human approval workflows", hint: "Founder sign-off gates by risk level." },
];

const GOV_HIPAA = [
  { label: "Encryption at rest",         state: "ok",    detail: "AES-256 · KMS-managed" },
  { label: "Encryption in transit",      state: "ok",    detail: "TLS 1.3 everywhere" },
  { label: "Access controls (RBAC)",     state: "ok",    detail: "Practice-scoped tokens · framework ready" },
  { label: "Audit logging",              state: "ok",    detail: "Structure defined · retention 7y" },
  { label: "PHI handling policy",        state: "watch", detail: "Beta: synthetic_only mode enforced" },
  { label: "Business Associate Agreements", state: "watch", detail: "MongoDB Atlas · LLM provider pending" },
  { label: "Breach response plan",       state: "watch", detail: "Draft policy · owner: CSCO" },
];

const GovernancePage = () => (
  <div>
    <PageTitle
      eyebrow="Governance"
      title="First-class governance"
      description="Executive policy, AI governance, security, HIPAA posture, audit, and human approval — treated as core, not an afterthought."
    />

    <Panel className="gov-hipaa">
      <div className="kg-block-head">
        <div className="eyebrow-mono">HIPAA Readiness</div>
        <KMono>7 of 7 tracked</KMono>
      </div>
      <div className="hipaa-list">
        {GOV_HIPAA.map((h) => (
          <div key={h.label} className={`hipaa-row hipaa-${h.state}`}>
            <StatusDot tone={h.state} />
            <div className="hipaa-l">{h.label}</div>
            <div className="hipaa-d">{h.detail}</div>
          </div>
        ))}
      </div>
    </Panel>

    <div className="gov-grid">
      {GOV_SECTIONS.map((g) => (
        <Panel key={g.k} className="gov-tile" data-testid={`gov-${g.k}`}>
          <div className="gov-t">{g.title}</div>
          <div className="gov-h">{g.hint}</div>
          <div className="gov-foot">Open</div>
        </Panel>
      ))}
    </div>
  </div>
);

/* ================================================================
   SETTINGS
   ================================================================ */
const SETTINGS_GROUPS = [
  { t: "Theme",                    s: "Charcoal · emerald · gold (executive default)" },
  { t: "Notifications",            s: "Email · in-app · webhook (unconfigured)" },
  { t: "Security",                 s: "SSO · MFA · session policy" },
  { t: "Future AI Models",         s: "Register model providers · pluggable routing" },
  { t: "API Management",           s: "Keys · webhooks · rate limits" },
  { t: "Executive Configuration",  s: "Officer scopes · quorum rules · escalation ladder" },
  { t: "System Configuration",     s: "Region · retention · environment mode" },
];

const SettingsPage = () => (
  <div>
    <PageTitle
      eyebrow="Settings"
      title="Configuration"
      description="System-wide configuration groups. Individual editors attach in future releases."
    />
    <div className="settings-list-eio">
      {SETTINGS_GROUPS.map((g) => (
        <Panel key={g.t} className="settings-row">
          <div>
            <div className="settings-t">{g.t}</div>
            <div className="settings-s">{g.s}</div>
          </div>
          <div className="settings-open">Configure <ChevronRight size={12} /></div>
        </Panel>
      ))}
    </div>
  </div>
);

/* ================================================================
   SHELL LAYOUT
   ================================================================ */
const ShellLayout = ({ children, fullscreen }) => {
  const navigate = useNavigate();
  const loc = useLocation();
  useEffect(() => { document.getElementById("emergent-badge")?.remove(); }, []);

  // Environment indicator (from URL host)
  const env = typeof window !== "undefined" && window.location.hostname.endsWith("camelionai.com") ? "PROD" : "PREVIEW";

  return (
    <div className={`eio-shell ${fullscreen ? "eio-shell-full" : ""}`} data-testid="eio-shell">
      <aside className="eio-side">
        <div className="eio-brand" onClick={() => navigate("/app")} data-testid="eio-brand">
          <img src="/brand/camelion-emblem.png" alt="" className="eio-brand-mark" />
          <div>
            <div className="eio-brand-name">Camelion<span className="accent">AI</span></div>
            <div className="eio-brand-sub">Executive Intelligence Platform</div>
          </div>
        </div>

        <nav className="eio-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.key}
              to={n.path}
              end={n.path === "/app"}
              className={({ isActive }) => `eio-link ${isActive ? "is-active" : ""}`}
              data-testid={`eio-nav-${n.key}`}
            >
              <n.Icon size={15} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="eio-side-foot">
          <div className="eio-env">
            <StatusDot tone={env === "PROD" ? "ok" : "info"} />
            <span>{env}</span>
            <span className="eio-env-sep">·</span>
            <span>v1.0</span>
          </div>
          <div className="eio-user">
            <div className="eio-user-avatar">RS</div>
            <div>
              <div className="eio-user-name">Dr. Ronik Seecharan</div>
              <div className="eio-user-role">Founder · CamelionAI</div>
            </div>
          </div>
        </div>
      </aside>

      <main className="eio-main" data-testid="eio-main" data-path={loc.pathname}>
        <div className="eio-synthetic-banner" data-testid="synthetic-data-banner">
          <FlaskConical size={12} />
          <span>HYBRID MODE — officer narratives are generated by live AI models (GPT · Claude · Gemini). Positions, confidence scores, and dashboard metrics remain deterministic demonstration logic.</span>
        </div>
        {children}
      </main>
    </div>
  );
};

/* ================================================================
   ROUTER
   ================================================================ */
const AppShell = () => {
  const loc = useLocation();
  const isTopology = loc.pathname.startsWith("/app/neural-topology");
  return (
    <ShellLayout fullscreen={isTopology}>
      <Routes>
        <Route path="/"                       index element={<ExecIntelligencePage />} />
        <Route path="divisions"                     element={<DivisionsPage />} />
        <Route path="workspace"                     element={<WorkspaceRoute />} />
        <Route path="workspace/:id"                 element={<DeliberationRoute />} />
        <Route path="founder-review"                element={<Navigate to="/app/workspace" replace />} />
        <Route path="neural-topology"               element={<TopologyRoute />} />
        <Route path="knowledge-graph"               element={<GraphPage />} />
        <Route path="institutional-memory"          element={<MemoryRoute />} />
        <Route path="governance"                    element={<GovernancePage />} />
        <Route path="settings"                      element={<SettingsPage />} />
        <Route path="*"                             element={<Navigate to="/app" replace />} />
      </Routes>
    </ShellLayout>
  );
};

export default AppShell;
